import moment from 'moment';
import { getLocalStorageItemWithFallback } from '@core/js/utils/cacheUtils';
import { isQuickshop } from '../utils/quickshopUtils';
import { getTargetedProduct } from '../utils/utils';

export function getProductCategory(product) {
    const browseCategory = _.get(product, 'browseCategory');
    const parentCategoryId = _.get(product, 'product.parentCategory.id');
    const defaultParentCategoryId = _.get(product, 'product.parentCategoryId');
    const strategyName = _.get(product, 'strategyName');
    const isBrowsedCategory = _.get(product, 'isBrowsedCategory');
    const mtoParentCategoryId = _.get(product, 'parentCategory.id');
    const productAncestors = _.get(product, 'productAncestors');

    if (strategyName) {
        return strategyName;
    } else if (isBrowsedCategory && productAncestors) {
        return productAncestors;
    } else if (browseCategory) {
        return browseCategory;
    } else if (parentCategoryId) {
        return parentCategoryId;
    } else if (defaultParentCategoryId) {
        return defaultParentCategoryId;
    } else if (mtoParentCategoryId) {
        return mtoParentCategoryId;
    }

    return 'DIRECT';
}

/**
 * Build deliveryDate object from HTML5 date input string
 * @param {string} date     Date string
 * @returns {object}        deliveryDate
 */
export function buildDateFromHTML5(date) {
    const deliveryDate = {};
    const dateArr = date.split('-');

    deliveryDate.year = parseInt(dateArr[0], 10);
    deliveryDate.month = parseInt(dateArr[1], 10);
    deliveryDate.day = parseInt(dateArr[2], 10);

    return deliveryDate;
}

/**
 * Build eGiftCard object if isEgiftCard from formValidation object
 * @param {boolean} isEgiftCard     Is this an e-gift card?
 * @param {object} formValidation   Validated form object
 * @returns {object|null} eGiftCard form values
 */
export function buildEgiftCard(isEgiftCard, formValidation) {
    if (isEgiftCard) {
        return {
            values: formValidation.values,
            dateFormats: formValidation.dateFormats
        };
    }
    return null;
}

/**
 * Build giftCard object if isEgiftCard from formValidation object
 * @param {boolean} isGiftCard      Is this a gift card?
 * @param {object} formValidation   Validated form object
 * @returns {object|null}           egiftCard form values
 */
export function buildGiftCard(isGiftCard, formValidation) {
    if (isGiftCard) {
        return {
            values: formValidation.values,
            dateFormats: formValidation.dateFormats
        };
    }
    return null;
}

/**
 * Gets the navigation id for analytics purposes to be
 * passed along when a item is added to cart.
 * @param {object} state Redux state object
 * @return {string} Navigation ID
 */
export function getNavId(state) {
    if (isQuickshop(state)) {
        // eslint-disable-next-line no-use-before-define
        return cartUtils.getQuickshopNavId(state);
    }

    // eslint-disable-next-line no-use-before-define
    return cartUtils.getProductNavId(state);
}

/**
 * Gets the mad mobile sales attributes for credit purposes to be
 * passed along when an item is added to cart.
 * @param {object} state redux state
 * @param {string} productSlug product slug
 * @return {string} sales attributes
 */
export function getSalesAttributes(state, productSlug) {
    const quickshopSource = _.get(state, 'quickshop.source', '');
    const salesAttributesLocalStorage = getLocalStorageItemWithFallback(productSlug);
    let salesAttributes = _.get(salesAttributesLocalStorage, 'salesAttributes', '');

    if (quickshopSource === 'wishlist') {
        salesAttributes = _.get(state, 'quickshop.salesAttributes', '');
    }

    return salesAttributes;
}

/* eslint-disable complexity */
/**
 * Gets the navigation id for analytics purposes to be
 * passed along when a item is added to cart from pdp.
 * @param  {function} state Redux state function
 * @return {string} Navigation ID
 */
function getProductNavId(state) {
    const analyticsContainer = _.get(state, 'core.localizations.containers.analyticsContainer', {});
    const targetProduct = getTargetedProduct(state);
    const wishlist = state.wishlist;
    const wishlistOwner = wishlist && wishlist.isOwner;
    const wishlistShopper = wishlist && !wishlist.isOwner;

    if (wishlistOwner) {
        return _.get(analyticsContainer, 'wishlist_cart-owner', '');
    } else if (wishlistShopper) {
        return _.get(analyticsContainer, 'wishlist_cart-shopper', '');
    }

    return getProductCategory(targetProduct);
}
/* eslint-enable complexity */

/* eslint-disable complexity */
/**
 * Gets the navigation id for analytics purposes to be
 * passed along when an item is added to cart from quickshop.
 * @param  {function} state  Redux state function
 * @returns {string}             Navigation ID
 */
function getQuickshopNavId(state) {
    const analyticsContainer = _.get(state, 'core.localizations.containers.analyticsContainer', {});
    const category = state.category;
    const quickshop = state.quickshop;

    if (category && category.isSearch) {
        const slug = _.get(analyticsContainer, 'category_search-results', category.searchTerm);

        return slug;
    } else if (quickshop && quickshop.source === 'registry') {
        return quickshop.ownerView ? analyticsContainer['registry_cart-owner'] : analyticsContainer['registry_cart-shopper'];
    } else if (quickshop && quickshop.source === 'wishlist') {
        return quickshop.ownerView ? analyticsContainer['wishlist_cart-owner'] : analyticsContainer['wishlist_cart-shopper'];
    }

    // eslint-disable-next-line no-use-before-define
    return cartUtils.getProductNavId(state);
}
/* eslint-enable complexity */

export function normalizeDate(localizedDateString, localDateFormatString) {
    const normalizedDateFormat = 'YYYY-MM-DD';
    const localizedMoment = moment(localizedDateString, localDateFormatString);
    const normalizedDateString = localizedMoment.format(normalizedDateFormat);

    return normalizedDateString;
}

export function setPriceForQuantity(confirmOptions) {
    if (!_.isEmpty(confirmOptions)) {
        const price = _.get(confirmOptions, 'confirmViewModel.price', 0);
        const quantity = _.get(confirmOptions, 'confirmViewModel.quantity', 1);
        const priceForQuantity = parseFloat((price * quantity).toFixed(2));

        return Object.assign({}, confirmOptions, { priceForQuantity });
    }
    return null;
}

const cartUtils = {
    buildDateFromHTML5,
    buildEgiftCard,
    buildGiftCard,
    getNavId,
    getProductNavId,
    getQuickshopNavId
};

export default cartUtils;
